// frontend/src/components/tables/PlayersTable.jsx

import React, { useRef, useState } from "react";
import Icon from "../common/Icon";
import StatusBadge from "../common/StatusBadge";
import {
  AGE_GROUP_LABELS,
  GENDER_LABELS,
  PLAYER_STATUS_LABELS,
} from "../../utils/constants";
import {
  uploadPlayerPhotoApi,
  uploadPlayerMedicalCertificateApi,
} from "../../api/player.api";

export default function PlayersTable({
  players = [],
  showClub = false,
  onEdit,
  onDelete,
  onToggleStatus, // ← جدید
  onReject, // رد بازیکنِ در انتظار تایید
  onRefresh,
  canManageStatus = true, // فقط مدیر سیستم اجازه‌ی تغییر وضعیت (تایید/رد) بازیکن را دارد
}) {
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const activePlayerId = useRef(null);

  const [uploading, setUploading] = useState(null);
  const [hoveredPlayer, setHoveredPlayer] = useState(null);
  const [previewPlayer, setPreviewPlayer] = useState(null);

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;

    if (
      photoPath.startsWith("http://") ||
      photoPath.startsWith("https://")
    ) {
      return photoPath;
    }

    const baseURL =
      process.env.REACT_APP_API_BASE_URL?.replace("/api/v1", "") || "";

    return `${baseURL}${photoPath}`;
  };

  const triggerUpload = (playerId, inputRef) => {
    activePlayerId.current = playerId;

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const handleFileChange = async (event, uploadFunction, type) => {
    const file = event.target.files?.[0];

    if (!file || !activePlayerId.current) return;

    const playerId = activePlayerId.current;

    try {
      setUploading(`${type}-${playerId}`);

      await uploadFunction(playerId, file);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(null);
      event.target.value = "";
      activePlayerId.current = null;
    }
  };

  const getStatus = (status) => {
    const statusMap = {
      PENDING: "pending",
      ACTIVE: "active",
      REJECTED: "inactive",
      INACTIVE: "inactive",
      INJURED: "pending",
    };

    return (
      <StatusBadge
        status={statusMap[status] || "inactive"}
        label={PLAYER_STATUS_LABELS[status] || status}
      />
    );
  };

  const getInitials = (player) => {
    const first = player?.firstName?.charAt(0) || "";
    const last = player?.lastName?.charAt(0) || "";

    return `${first}${last}` || "?";
  };

  return (
    <div className="relative">
      {/* Hidden inputs */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) =>
          handleFileChange(
            event,
            uploadPlayerPhotoApi,
            "photo"
          )
        }
      />

      <input
        ref={documentInputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={(event) =>
          handleFileChange(
            event,
            uploadPlayerMedicalCertificateApi,
            "document"
          )
        }
      />

      {/* ================= DESKTOP ================= */}
      <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
        <table className="w-full border-collapse text-right">
          <thead>
            <tr className="border-b border-slate-200 bg-gradient-to-l from-slate-50 via-white to-emerald-50/40">
              <th className="px-5 py-4 text-xs font-black text-slate-500">
                بازیکن
              </th>

              {showClub && (
                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  باشگاه
                </th>
              )}

              <th className="px-5 py-4 text-xs font-black text-slate-500">
                گروه سنی
              </th>

              <th className="px-5 py-4 text-xs font-black text-slate-500">
                جنسیت
              </th>

              <th className="px-5 py-4 text-xs font-black text-slate-500">
                پست
              </th>

              <th className="px-5 py-4 text-xs font-black text-slate-500">
                شماره
              </th>

              <th className="px-5 py-4 text-xs font-black text-slate-500">
                وضعیت
              </th>

              <th className="px-5 py-4 text-center text-xs font-black text-slate-500">
                عملیات
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {players.map((player, index) => {
              const photoUrl = getPhotoUrl(player.photo);
              const isHovered = hoveredPlayer === player.id;

              return (
                <tr
                  key={player.id}
                  onMouseEnter={() =>
                    setHoveredPlayer(player.id)
                  }
                  onMouseLeave={() =>
                    setHoveredPlayer(null)
                  }
                  className="group transition-all duration-300 hover:bg-emerald-50/40"
                  style={{
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  {/* PLAYER */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          photoUrl &&
                          setPreviewPlayer(player)
                        }
                        className={`
                          relative h-12 w-12 shrink-0
                          overflow-hidden rounded-full
                          bg-gradient-to-br
                          from-emerald-400
                          via-emerald-500
                          to-emerald-700
                          shadow-lg
                          shadow-emerald-500/20
                          transition-all duration-300
                          ${
                            isHovered
                              ? "scale-110 shadow-emerald-500/40"
                              : ""
                          }
                        `}
                      >
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={`${player.firstName || ""} ${
                              player.lastName || ""
                            }`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-sm font-black text-white">
                            {getInitials(player)}
                          </span>
                        )}

                        {photoUrl && (
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
                            <Icon
                              name="eye"
                              className="h-4 w-4"
                            />
                          </span>
                        )}
                      </button>

                      <div className="min-w-0">
                        <div className="font-black text-slate-800">
                          {player.firstName}{" "}
                          {player.lastName}
                        </div>

                        <div className="mt-1 text-[11px] text-slate-400">
                          کد ملی:{" "}
                          {player.nationalCode ? (
                            <span dir="ltr" className="font-mono tracking-wider text-slate-500">
                              {player.nationalCode}
                            </span>
                          ) : (
                            <span className="rounded-full bg-red-50 px-2 py-0.5 font-bold text-red-500">
                              ثبت نشده — الزامی
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* CLUB */}
                  {showClub && (
                    <td className="px-5 py-4">
                      <span className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
                        {player.club?.name || "-"}
                      </span>
                    </td>
                  )}

                  {/* AGE */}
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-slate-600">
                      {AGE_GROUP_LABELS[
                        player.ageGroup
                      ] ||
                        player.ageGroup ||
                        "-"}
                    </span>
                  </td>

                  {/* GENDER */}
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-slate-600">
                      {GENDER_LABELS[player.gender] ||
                        player.gender ||
                        "-"}
                    </span>
                  </td>

                  {/* POSITION */}
                  <td className="px-5 py-4">
                    <span className="inline-flex rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                      {player.position || "-"}
                    </span>
                  </td>

                  {/* JERSEY */}
                  <td className="px-5 py-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-900 text-sm font-black text-white shadow-md">
                      {player.jerseyNumber || "-"}
                    </div>
                  </td>

                  {/* STATUS */}
                  <td className="px-5 py-4">
                    {getStatus(player.status)}
                  </td>

                  {/* ===== ACTIONS ===== */}
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-center gap-1">
                      {/* آپلود عکس */}
                      <button
                        type="button"
                        onClick={() =>
                          triggerUpload(
                            player.id,
                            photoInputRef
                          )
                        }
                        disabled={
                          uploading ===
                          `photo-${player.id}`
                        }
                        title="تعویض تصویر"
                        className="grid h-8 w-8 place-items-center rounded-xl text-emerald-600 transition hover:scale-110 hover:bg-emerald-100"
                      >
                        {uploading ===
                        `photo-${player.id}` ? (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                        ) : (
                          <Icon
                            name="camera"
                            className="h-3.5 w-3.5"
                          />
                        )}
                      </button>

                      {/* آپلود مدرک */}
                      <button
                        type="button"
                        onClick={() =>
                          triggerUpload(
                            player.id,
                            documentInputRef
                          )
                        }
                        disabled={
                          uploading ===
                          `document-${player.id}`
                        }
                        title="آپلود مدرک پزشکی"
                        className="grid h-8 w-8 place-items-center rounded-xl text-violet-600 transition hover:scale-110 hover:bg-violet-100"
                      >
                        {uploading ===
                        `document-${player.id}` ? (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
                        ) : (
                          <Icon
                            name="document"
                            className="h-3.5 w-3.5"
                          />
                        )}
                      </button>

                      {/* ویرایش */}
                      <button
                        type="button"
                        onClick={() =>
                          onEdit?.(player)
                        }
                        title="ویرایش"
                        className="grid h-8 w-8 place-items-center rounded-xl text-blue-600 transition hover:scale-110 hover:bg-blue-100"
                      >
                        <Icon
                          name="edit"
                          className="h-3.5 w-3.5"
                        />
                      </button>

                      {/* ===== تغییر وضعیت (تایید/فعال/غیرفعال) — فقط مدیر سیستم ===== */}
                      {canManageStatus && (
                        <button
                          type="button"
                          onClick={() =>
                            onToggleStatus?.(player.id, player.status)
                          }
                          title={
                            player.status === "ACTIVE"
                              ? "غیرفعال‌ سازی"
                              : player.status === "PENDING"
                              ? "تایید بازیکن"
                              : "فعال‌ سازی"
                          }
                          className={`grid h-8 w-8 place-items-center rounded-xl transition hover:scale-110 ${
                            player.status === "ACTIVE"
                              ? "text-amber-600 hover:bg-amber-100"
                              : "text-emerald-600 hover:bg-emerald-100"
                          }`}
                        >
                          {player.status === "ACTIVE" ? (
                            <Icon name="pause" className="h-3.5 w-3.5" />
                          ) : (
                            <Icon name="play" className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}

                      {/* ===== رد بازیکنِ در انتظار تایید — فقط مدیر سیستم ===== */}
                      {canManageStatus && player.status === "PENDING" && (
                        <button
                          type="button"
                          onClick={() => onReject?.(player.id)}
                          title="رد بازیکن"
                          className="grid h-8 w-8 place-items-center rounded-xl text-red-500 transition hover:scale-110 hover:bg-red-100"
                        >
                          <Icon name="close" className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* غیرفعال‌سازی (حذف) */}
                      <button
                        type="button"
                        onClick={() =>
                          onDelete?.(player.id)
                        }
                        title="غیرفعال سازی کامل"
                        className="grid h-8 w-8 place-items-center rounded-xl text-red-500 transition hover:scale-110 hover:bg-red-100"
                      >
                        <Icon
                          name="trash"
                          className="h-3.5 w-3.5"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE ================= */}
      <div className="grid grid-cols-2 gap-3 md:hidden">
        {players.map((player) => {
          const photoUrl = getPhotoUrl(player.photo);

          return (
            <div
              key={player.id}
              className="group rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-1 hover:border-emerald-200 hover:shadow-lg"
            >
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() =>
                    photoUrl &&
                    setPreviewPlayer(player)
                  }
                  className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 shadow-md"
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={`${player.firstName} ${player.lastName}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-sm font-black text-white">
                      {getInitials(player)}
                    </span>
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-slate-800">
                    {player.firstName}{" "}
                    {player.lastName}
                  </h3>

                  <p className="mt-0.5 truncate text-[10px] text-slate-400">
                    {player.position || "بدون پست"}
                  </p>

                  <div className="mt-1 flex items-center gap-1">
                    <StatusBadge
                      status={
                        player.status === "ACTIVE"
                          ? "active"
                          : player.status === "INJURED" || player.status === "PENDING"
                          ? "pending"
                          : "inactive"
                      }
                      label=""
                      className="!px-2 !py-0.5 text-[9px]"
                    />

                    {player.jerseyNumber && (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[9px] font-black text-white">
                        {player.jerseyNumber}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-2.5 flex items-center justify-between border-t border-slate-100 pt-2">
                <div className="text-[9px] text-slate-400">
                  {AGE_GROUP_LABELS[
                    player.ageGroup
                  ] || player.ageGroup}
                  <span className="mx-1">•</span>
                  {GENDER_LABELS[player.gender] ||
                    player.gender}
                </div>

                <div className="flex gap-0.5">
                  <button
                    type="button"
                    onClick={() =>
                      triggerUpload(
                        player.id,
                        photoInputRef
                      )
                    }
                    className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"
                    title="تعویض تصویر"
                  >
                    <Icon
                      name="camera"
                      className="h-3.5 w-3.5"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onEdit?.(player)
                    }
                    className="rounded p-1.5 text-blue-600 hover:bg-blue-50"
                    title="ویرایش"
                  >
                    <Icon
                      name="edit"
                      className="h-3.5 w-3.5"
                    />
                  </button>

                  {canManageStatus && (
                    <button
                      type="button"
                      onClick={() =>
                        onToggleStatus?.(player.id, player.status)
                      }
                      className={`rounded p-1.5 ${
                        player.status === "ACTIVE"
                          ? "text-amber-600 hover:bg-amber-50"
                          : "text-emerald-600 hover:bg-emerald-50"
                      }`}
                      title={
                        player.status === "ACTIVE"
                          ? "غیرفعال‌ سازی"
                          : "فعال‌ سازی"
                      }
                    >
                      {player.status === "ACTIVE" ? (
                        <Icon name="pause" className="h-3.5 w-3.5" />
                      ) : (
                        <Icon name="play" className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      onDelete?.(player.id)
                    }
                    className="rounded p-1.5 text-red-500 hover:bg-red-50"
                    title="غیرفعال‌ سازی کامل"
                  >
                    <Icon
                      name="trash"
                      className="h-3.5 w-3.5"
                    />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= PHOTO PREVIEW ================= */}
      {previewPlayer && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => setPreviewPlayer(null)}
        >
          <div
            className="relative max-w-sm overflow-hidden rounded-3xl bg-white p-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewPlayer(null)}
              className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-red-500"
            >
              <Icon
                name="close"
                className="h-4 w-4"
              />
            </button>

            <img
              src={getPhotoUrl(previewPlayer.photo)}
              alt={`${previewPlayer.firstName} ${previewPlayer.lastName}`}
              className="max-h-[70vh] w-full rounded-2xl object-contain"
            />

            <div className="px-2 pb-2 pt-3 text-center">
              <h3 className="font-black text-slate-800">
                {previewPlayer.firstName}{" "}
                {previewPlayer.lastName}
              </h3>

              <p className="mt-1 text-xs text-slate-400">
                {previewPlayer.position ||
                  "بدون پست"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}